import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useProductShippingMetadataMutations, useProductShippingMetadataDetail } from "../../../hooks/useProductShippingMetadata";

interface ProductShippingMetadataModalProps {
  open: boolean;
  onClose: () => void;
  editMode: { productId: string; retailer: string } | null;
}

const ProductShippingMetadataModal: React.FC<ProductShippingMetadataModalProps> = ({
  open,
  onClose,
  editMode,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    productId: "",
    retailer: "amazon",
    productTitle: "",
    price: "",
    weightInKg: "",
    volumeInCubicInches: "",
    fixedShippingPrice: "",
    source: "manual",
  });

  const { data: existingData } = useProductShippingMetadataDetail(
    editMode?.productId || "",
    editMode?.retailer || "",
    !!editMode
  );

  const { createMetadata, updateMetadata } = useProductShippingMetadataMutations();

  useEffect(() => {
    if (editMode && existingData?.data) {
      const data = existingData.data;
      setFormData({
        productId: data.productId,
        retailer: data.retailer,
        productTitle: data.productTitle || "",
        price: data.price?.toString() || "",
        weightInKg: data.weightInKg?.toString() || "",
        volumeInCubicInches: data.volumeInCubicInches?.toString() || "",
        fixedShippingPrice: data.fixedShippingPrice?.toString() || "",
        source: data.source || "manual",
      });
    } else {
      setFormData({
        productId: "",
        retailer: "amazon",
        productTitle: "",
        price: "",
        weightInKg: "",
        volumeInCubicInches: "",
        fixedShippingPrice: "",
        source: "manual",
      });
    }
  }, [editMode, existingData]);

  const handleSubmit = async () => {
    const payload = {
      productId: formData.productId,
      retailer: formData.retailer,
      productTitle: formData.productTitle || undefined,
      price: formData.price ? parseFloat(formData.price) : undefined,
      weightInKg: formData.weightInKg ? parseFloat(formData.weightInKg) : undefined,
      volumeInCubicInches: formData.volumeInCubicInches ? parseFloat(formData.volumeInCubicInches) : undefined,
      fixedShippingPrice: formData.fixedShippingPrice ? parseFloat(formData.fixedShippingPrice) : undefined,
      source: formData.source,
    };

    if (editMode) {
      await updateMetadata.mutateAsync({
        productId: editMode.productId,
        retailer: editMode.retailer,
        data: payload,
      });
    } else {
      await createMetadata.mutateAsync(payload);
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editMode ? t("admin.shippingMetadata.editShippingMetadata") : t("admin.shippingMetadata.createShippingMetadata")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.shippingMetadata.productId")}
              value={formData.productId}
              onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
              disabled={!!editMode}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.shippingMetadata.retailer")}</InputLabel>
              <Select
                value={formData.retailer}
                label={t("admin.shippingMetadata.retailer")}
                onChange={(e) => setFormData({ ...formData, retailer: e.target.value })}
                disabled={!!editMode}
              >
                <MenuItem value="amazon">Amazon</MenuItem>
                <MenuItem value="walmart">Walmart</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("admin.shippingMetadata.productTitle")}
              value={formData.productTitle}
              onChange={(e) => setFormData({ ...formData, productTitle: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.shippingMetadata.priceLabel")}
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.shippingMetadata.fixedShippingPriceLabel")}
              type="number"
              value={formData.fixedShippingPrice}
              onChange={(e) => setFormData({ ...formData, fixedShippingPrice: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.shippingMetadata.weightLabel")}
              type="number"
              value={formData.weightInKg}
              onChange={(e) => setFormData({ ...formData, weightInKg: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.shippingMetadata.volumeLabel")}
              type="number"
              value={formData.volumeInCubicInches}
              onChange={(e) => setFormData({ ...formData, volumeInCubicInches: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.shippingMetadata.source")}</InputLabel>
              <Select
                value={formData.source}
                label={t("admin.shippingMetadata.source")}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              >
                <MenuItem value="manual">{t("admin.shippingMetadata.sourceManual")}</MenuItem>
                <MenuItem value="admin">{t("admin.shippingMetadata.sourceAdmin")}</MenuItem>
                <MenuItem value="zinc_api">{t("admin.shippingMetadata.sourceZincApi")}</MenuItem>
                <MenuItem value="product_fetch">{t("admin.shippingMetadata.sourceProductFetch")}</MenuItem>
                <MenuItem value="product_refresh">{t("admin.shippingMetadata.sourceProductRefresh")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {editMode ? t("common.update") : t("common.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductShippingMetadataModal;

